import React from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ProductPerformance } from './productTypes';
import { formatCurrency } from '../FinancialDashboardController';

interface Props {
  performances: ProductPerformance[];
  fromDate: Date | null;
  toDate: Date | null;
}

const ExportProductsButton: React.FC<Props> = ({ performances, fromDate, toDate }) => {
  const { t } = useTranslation();

  const handleExport = () => {
    if (performances.length === 0) {
      return;
    }

    const escapeCSVValue = (value: any): string => {
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const rangeLabel = (fromDate || toDate)
      ? `${fromDate ? format(fromDate, 'MM/yyyy') : '...'} – ${toDate ? format(toDate, 'MM/yyyy') : '...'}`
      : t('productsdashboard.all-data');

    const metaRows = [
      `${escapeCSVValue(t('productsdashboard.export.date-range'))},${escapeCSVValue(rangeLabel)}`,
      '',
    ];

    const headers = [
      t('productsdashboard.table.product-code'),
      t('productsdashboard.table.product-title'),
      t('productsdashboard.table.total-units'),
      t('productsdashboard.table.vat'),
      t('productsdashboard.table.total-revenue'),
      t('productsdashboard.table.revenue-ex-vat'),
      t('productsdashboard.table.vat-amount'),
      t('productsdashboard.table.avg-units-period'),
      t('productsdashboard.table.avg-revenue-period'),
    ];

    const rows = performances.map((p) => [
      p.ProductCode,
      p.ProductTitle,
      p.TotalUnits,
      `${p.VAT}%`,
      formatCurrency(p.TotalRevenue),
      formatCurrency(p.TotalRevenueExVAT),
      formatCurrency(p.TotalRevenue - p.TotalRevenueExVAT),
      p.AvgUnitsPerPeriod.toFixed(1),
      formatCurrency(p.AvgRevenuePerPeriod),
    ]);

    const csvContent = [
      ...metaRows,
      headers.map(escapeCSVValue).join(','),
      ...rows.map((row) => row.map(escapeCSVValue).join(',')),
    ].join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `product-performance-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <button
      onClick={handleExport}
      disabled={performances.length === 0}
      className="export-products-btn"
    >
      {t('productsdashboard.export-products')}
    </button>
  );
};

export default ExportProductsButton;
